import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';

const OVERRIDE_ACTION_KEYS = [
  {
    value: 'change_loading_point',
    labelKey: 'dm_override_action_change_loading',
    disabled: false,
  },
  {
    value: 'change_dumping_point',
    labelKey: 'dm_override_action_change_dumping',
    disabled: false,
  },
  {
    value: 'change_route',
    labelKey: 'dm_override_action_change_route',
    disabled: true,
  },
  {
    value: 'end_trip_maintenance',
    labelKey: 'dm_override_action_end_trip',
    disabled: true,
  },
];

const DESTINATIONS = [
  'Dump Zone A',
  'Dump Zone B',
  'Crusher 1',
  'Crusher 2',
  'Base Site',
];

const DESTINATION_LABEL_KEYS: Record<string, string> = {
  'Dump Zone A': 'dm_override_destination_dump_zone_a',
  'Dump Zone B': 'dm_override_destination_dump_zone_b',
  'Crusher 1': 'dm_override_destination_crusher_1',
  'Crusher 2': 'dm_override_destination_crusher_2',
  'Base Site': 'dm_override_destination_base_site',
};

const APPLY_FOR_KEYS = [
  { value: 'current', labelKey: 'dm_override_apply_current_trip' },
  { value: 'next', labelKey: 'dm_override_apply_next_trip' },
  { value: 'all_trips_from_next', labelKey: 'dm_override_apply_all_trips' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  initialTruckId?: string;
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 600,
      color: 'text.secondary',
      mb: '6px',
    }}
  >
    {children}
  </Typography>
);

const selectSx = {
  fontSize: '12px',
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
};

const OverridePlanModal: React.FC<Props> = ({
  open,
  onClose,
  initialTruckId,
}) => {
  const { t } = useTranslation();
  const assets = useAppSelector(selectAssociatedAssets);
  const api = new DispatchMonitorAPI();

  const [selectedAsset, setSelectedAsset] = useState(initialTruckId ?? '');
  const [action, setAction] = useState('');
  const [destination, setDestination] = useState('');
  const [applyFor, setApplyFor] = useState('current');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedAsset(initialTruckId ?? '');
      setAction('');
      setDestination('');
      setApplyFor('current');
      setReason('');
      setSubmitError(null);
      setSubmitted(false);
    }
  }, [open, initialTruckId]);

  const handleSubmit = async () => {
    if (!selectedAsset || !action || !destination) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.sendOverridePlan({
        device: selectedAsset,
        overrideAction: action,
        newDestination: destination,
        applyFor,
        reason: reason.trim() || undefined,
      });
      setSubmitted(true);
      setTimeout(onClose, 1200);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ?? t('dm_override_submit_error')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    Boolean(selectedAsset && action && destination) &&
    !submitting &&
    !submitted;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.main',
          borderRadius: '12px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.24)',
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: '16px 20px 12px',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Box
          sx={{
            width: '32px',
            height: '32px',
            borderRadius: '6px',
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            flexShrink: 0,
          }}
        >
          ⚠
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.2 }}
          >
            {t('dm_override_title')}
          </Typography>
          <Typography
            sx={{ fontSize: '11px', color: 'text.secondary', mt: '3px' }}
          >
            {t('dm_override_subtitle')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          ✕
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
        }}
      >
        {/* Select Truck */}
        <Box>
          <FieldLabel>{t('dm_override_select_truck')}</FieldLabel>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={selectedAsset}
            onChange={(e) => {
              setSelectedAsset(e.target.value);
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: { bgcolor: 'background.paper' } } }}
            renderValue={(val) => {
              if (!val)
                return (
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    {t('dm_override_select_truck_placeholder')}
                  </Typography>
                );
              const a = assets.find(
                (x) => (x.device?.deviceId ?? x.bumperNumber) === val
              );
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '12px' }}>
                    {a?.bumperNumber ?? val}
                  </span>
                  {a?.operators?.[0]?.display && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '11px', color: 'text.secondary' }}
                    >
                      {a.operators[0].display}
                    </Typography>
                  )}
                </Box>
              );
            }}
          >
            {assets.map((a) => {
              const driver = a.operators?.[0]?.display;
              return (
                <MenuItem
                  key={a.device?.deviceId ?? a.bumperNumber}
                  value={a.device?.deviceId ?? a.bumperNumber}
                  sx={{
                    fontSize: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <span>{a.bumperNumber}</span>
                  {driver && (
                    <Typography
                      component="span"
                      sx={{ fontSize: '11px', color: 'text.secondary' }}
                    >
                      {driver}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </Box>

        {/* Override Action */}
        <Box>
          <FieldLabel>{t('dm_override_action')}</FieldLabel>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: { bgcolor: 'background.paper' } } }}
            renderValue={(val) => {
              if (!val)
                return (
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    {t('dm_override_action_placeholder')}
                  </Typography>
                );
              return (
                <Typography sx={{ fontSize: '12px' }}>
                  {t(
                    OVERRIDE_ACTION_KEYS.find((o) => o.value === val)
                      ?.labelKey ?? ''
                  )}
                </Typography>
              );
            }}
          >
            {OVERRIDE_ACTION_KEYS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled}
                sx={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}
              >
                <Box
                  component="span"
                  sx={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    bgcolor: opt.disabled ? 'text.disabled' : 'primary.main',
                    flexShrink: 0,
                    mr: '8px',
                  }}
                />
                {t(opt.labelKey)}
                {opt.disabled && (
                  <Typography
                    component="span"
                    sx={{ fontSize: '10px', color: 'text.disabled', ml: '6px' }}
                  >
                    {t('dm_override_unavailable')}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* New Destination */}
        <Box>
          <FieldLabel>{t('dm_override_new_destination')}</FieldLabel>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
            }}
            sx={selectSx}
            MenuProps={{ PaperProps: { sx: { bgcolor: 'background.paper' } } }}
            renderValue={(val) => {
              if (!val)
                return (
                  <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    {t('dm_override_destination_placeholder')}
                  </Typography>
                );
              return (
                <Typography sx={{ fontSize: '12px' }}>
                  {t(DESTINATION_LABEL_KEYS[val] ?? val)}
                </Typography>
              );
            }}
          >
            {DESTINATIONS.map((d) => (
              <MenuItem key={d} value={d} sx={{ fontSize: '12px' }}>
                {t(DESTINATION_LABEL_KEYS[d])}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Apply For */}
        <Box>
          <FieldLabel>{t('dm_override_apply_for')}</FieldLabel>
          <RadioGroup
            value={applyFor}
            onChange={(e) => {
              setApplyFor(e.target.value);
            }}
            sx={{ gap: '2px' }}
          >
            {APPLY_FOR_KEYS.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={
                  <Radio
                    size="small"
                    sx={{
                      color: 'text.disabled',
                      '&.Mui-checked': { color: 'primary.main' },
                      padding: '4px 8px',
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '12px' }}>
                    {t(opt.labelKey)}
                  </Typography>
                }
              />
            ))}
          </RadioGroup>
        </Box>

        {/* Reason */}
        <Box>
          <FieldLabel>{t('dm_override_reason')}</FieldLabel>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={t('dm_override_reason_placeholder')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '12px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>

        {/* Error message */}
        {submitError && (
          <Typography
            sx={{
              fontSize: '11px',
              color: '#f87171',
              background: 'rgba(248,113,113,0.08)',
              border: '1px solid rgba(248,113,113,0.2)',
              borderRadius: '6px',
              padding: '8px 12px',
            }}
          >
            {submitError}
          </Typography>
        )}

        {/* Apply Override button */}
        <Box
          onClick={
            canSubmit
              ? () => {
                  void handleSubmit();
                }
              : undefined
          }
          sx={{
            bgcolor: submitted
              ? 'rgba(74,222,128,0.15)'
              : canSubmit
              ? 'primary.main'
              : 'action.disabledBackground',
            color: submitted ? '#4ade80' : canSubmit ? '#fff' : 'text.disabled',
            border: submitted ? '1px solid rgba(74,222,128,0.3)' : 'none',
            borderRadius: '8px',
            padding: '11px',
            textAlign: 'center',
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.05em',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            '&:hover': canSubmit ? { opacity: 0.85 } : {},
          }}
        >
          {submitting && (
            <Box
              sx={{
                width: '14px',
                height: '14px',
                border: '2px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
                '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
              }}
            />
          )}
          {submitted
            ? `✓ ${t('dm_override_btn_sent')}`
            : submitting
            ? t('dm_override_btn_sending')
            : t('dm_override_btn_apply')}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OverridePlanModal;
